/**
 * ExecutionDialog — Run Test Sheet (redesign)
 *
 * Single-screen run-test form built on shadcn Sheet + react-hook-form + Zod.
 * Replaces the previous two-modal design (ExecutionDialog + ConfigurationWizard
 * 5-step) — see plans/test-creation-redesign.md.
 *
 * Modes:
 *   - mode='create' (default)  → top-right "Run Test" / Flows page "Run".
 *   - mode='edit'              → row "Edit configuration"; pre-fills from `editingConfig`.
 *
 * Inside `create` mode, the user toggles between One-off (default) and Use saved.
 * Submitting fires runtimeService.executeFlow with `testRunId` so the backend
 * (QueueService.addToQueue + startExecution + completeExecution) syncs status
 * back to test_runs from Admin SDK — no client-side updateDoc(test_runs) that
 * the rules engine rejects.
 */

import { useEffect, useMemo, useState } from 'react';
import { executionContextFromConfig, esimRelocationPolicy } from '@simcheck/shared-types';
import { ESIM_MOVE_DECLINED, isEsimMoveApprovalError, useEsimMoveApproval } from '@/components/esim/EsimMoveApproval';
import { ModemPinPicker } from './ModemPinPicker';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/Checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { TimeZonePicker } from '@/components/ui/TimeZonePicker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Separator } from '@/components/ui/separator';
import {
  PlayCircle, Save, ChevronDown, Check, Workflow, FileCode2, Layers,
  MapPin, Wifi, Signal, Clock, Loader2, AlertCircle, ChevronsUpDown, Zap, Settings2, Tag, Radio,
} from 'lucide-react';
import {
  FlowInputFields, finalizeFlowInputs, ESIM_RESOLVED_INPUTS, type FlowInputDefinition,
} from '@/components/quick-tests/FlowInputsDialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { getFlows, getQuickTestFlows, isFlowRunnable } from '@/services/flowService';
import {
  getTestConfigurations, createTestConfiguration, createTestRun, updateTestConfiguration,
} from '@/services/testConfigurationService';
import { getGroupsForOrg, getProfilesForOrg, getSharedProfiles, sharedGroupsForOrg } from '@/services/esimService';
import { getLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { getClustersForOrg } from '@/services/clusterService';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import * as runtimeService from '@/services/runtime';
import { RatSelector } from '@/components/quick-tests/RatSelector';
import { ESimSelectionCombobox } from '@/components/quick-tests/ESimSelectionCombobox';
import {
  SHARED_PREFIX, encodeProfileSelection, parseESimSelection,
} from '@/utils/esimSelection';
import { HierarchicalLocationSelector } from '@/components/locations/HierarchicalLocationSelector';
import {
  CONNECTIVITY_OPTIONS, ConnectivityFields, EMPTY_CONNECTIVITY, buildCampaignConfig,
  parseConnectivityMode, validateConnectivity, type ConnectivityDraft,
} from './ConnectivityFields';
import { startCampaign } from '@/services/campaignService';
import {
  RunTestFormSchema, defaultsForCreate, defaultsForUseSaved, defaultsFromConfig,
  type RunTestFormValues,
} from './schema';
import type { Flow } from '@/types/firestore';
import {
  canOrgUseSharedEsims,
  isProfileReinstallable,
  parseSharedPoolSentinel,
  type TestConfiguration, type Location, type Cluster, type ESimGroup, type ESimProfile, type ForcedRat,
  type LocationRequirement, type ESimSelectionConfig, type ExecutionTiming,
} from '@simcheck/shared-types';

interface ExecutionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-select a flow (e.g. Flows page "Run" button). */
  preselectedFlow?: Flow | null;
  /** Pre-select a saved configuration (sets configMode='useSaved'). */
  preselectedConfig?: TestConfiguration | null;
  /** Edit mode: fully populate the form from this config. */
  editingConfig?: TestConfiguration | null;
  /** Notified with new run id on successful submit. */
  onRunStarted?: (runId: string) => void;
  /** Notified when the submitted run was a connectivity run, so the caller can open its
   *  own detail panel instead of the generic logs modal (#283). */
  onConnectivityStarted?: (runId: string) => void;
  /** Notified after a successful config edit save. */
  onEditSaved?: () => void;
}

export function ExecutionDialog({
  isOpen, onClose, preselectedFlow, preselectedConfig, editingConfig, onRunStarted,
  onConnectivityStarted, onEditSaved,
}: ExecutionDialogProps) {
  const { activeOrganization, currentUser, isSuperAdmin } = useAuth();
  const { success, error: showError, info } = useToast();
  const { confirmMove, dialog: esimMoveDialog } = useEsimMoveApproval();
  const isEdit = !!editingConfig;

  // ── Reference data ──────────────────────────────────────────────────────
  const [flows, setFlows] = useState<Flow[]>([]);
  const [configs, setConfigs] = useState<TestConfiguration[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [esimGroups, setEsimGroups] = useState<ESimGroup[]>([]);
  const [esimProfiles, setEsimProfiles] = useState<ESimProfile[]>([]);
  // deviceId → locationId, so the eSIM picker can say where a specific SIM
  // physically lives (a shared SIM can only run where its eUICC already is).
  const [deviceLocations, setDeviceLocations] = useState<Map<string, string | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // #283 — the connectivity-run fields. Kept outside the RHF schema because none of them are
  // shared with a flow run; the Flow picker's synthetic id is what selects this shape.
  const [connDraft, setConnDraft] = useState<ConnectivityDraft>(EMPTY_CONNECTIVITY);

  useEffect(() => {
    if (!isOpen || !activeOrganization) return;
    let cancelled = false;
    setLoading(true);
    const wantShared = canOrgUseSharedEsims(activeOrganization as any);
    Promise.all([
      getFlows(activeOrganization.id),
      getTestConfigurations(activeOrganization.id),
      getLocations(),
      getClustersForOrg(activeOrganization.id),
      getGroupsForOrg(activeOrganization.id),
      getProfilesForOrg(activeOrganization.id),
      getQuickTestFlows(activeOrganization.id),
      wantShared ? getSharedProfiles().catch(() => [] as ESimProfile[]) : Promise.resolve([] as ESimProfile[]),
      // Non-fatal: without it the picker just can't annotate residency.
      edgeDeviceService.getDevices().catch(() => [] as any[]),
    ]).then(([f, c, l, cl, eg, ep, qf, shared, devices]) => {
      if (cancelled) return;
      // Merge in system quick-test flows (org-scoped list excludes them) so
      // quick tests are pickable — and therefore schedulable — from /tests.
      const seen = new Set(f.map((x) => x.id));
      const merged = [...f, ...qf.filter((x) => !seen.has(x.id))];
      setFlows(merged);
      setConfigs(c);
      // Tenancy (#202): this list feeds a SELECTABLE LocationPicker, so an
      // unfiltered fetch offers another org's private sites — and the synthetic
      // demo "… Lab" sites, which have no hardware to run anything on.
      setLocations(filterLocationsByTenancy(l, activeOrganization.id, isSuperAdmin));
      setClusters(cl);
      setEsimGroups(eg);
      // Shared inventory profiles (for granted orgs) are appended so the single
      // eSIM picker can offer them under a "Shared eSIMs" group.
      const epSeen = new Set(ep.map((p) => p.id));
      setEsimProfiles([...ep, ...shared.filter((p) => !epSeen.has(p.id))]);
      setDeviceLocations(new Map((devices as any[]).map((d) => [d.id, d.locationId || null])));
    }).catch((err) => {
      if (cancelled) return;
      console.error('[ExecutionDialog] Failed to load reference data:', err);
      showError('Failed to load data');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, activeOrganization, showError]);

  // ── Form ────────────────────────────────────────────────────────────────
  const defaultValues = useMemo<RunTestFormValues>(() => {
    if (editingConfig) return defaultsFromConfig(editingConfig);
    if (preselectedConfig) return defaultsForUseSaved(preselectedConfig.id, preselectedConfig.flowId ?? '');
    const base = defaultsForCreate();
    if (preselectedFlow) base.flowId = preselectedFlow.id;
    return base;
  }, [editingConfig, preselectedConfig, preselectedFlow]);

  const form = useForm<RunTestFormValues>({
    resolver: zodResolver(RunTestFormSchema),
    defaultValues,
  });

  const configMode = form.watch('configMode');
  const flowId = form.watch('flowId');
  const watched = form.watch();

  // Only runnable flows (not draft / archived) are pickable for a Run or
  // Schedule — a draft is work-in-progress. The currently-selected flow is
  // always kept in the list so a preselected/edited draft still renders.
  const pickableFlows = useMemo(
    () => flows.filter((f) => isFlowRunnable((f as any).status) || f.id === flowId),
    [flows, flowId]
  );

  // Reset the form to fresh defaults every time the sheet opens. Without
  // this, RHF preserves state between opens — a user who picked Schedule +
  // typed a name would see the same selection on the next "New Run" click.
  useEffect(() => {
    if (isOpen) form.reset(defaultValues);
    // form is stable; defaultValues changes when preselect/edit inputs change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultValues]);

  // ── Submit ──────────────────────────────────────────────────────────────
  async function onSubmit(values: RunTestFormValues) {
    if (!activeOrganization || !currentUser) return;
    setSubmitting(true);
    try {
      // #283 — connectivity path. It holds modems rather than executing a flow, so it goes to
      // its own endpoint; everything before this point (name, location) is the shared chrome.
      const connMode = parseConnectivityMode(values.flowId);
      if (connMode) {
        const name = (values.saveAsName ?? '').trim();
        if (name.length < 3) {
          form.setError('saveAsName', { message: 'Name (min 3 chars) required' });
          return;
        }
        if (!values.locationId) {
          form.setError('locationId', { message: 'Pick a location' } as any);
          return;
        }
        const problem = validateConnectivity(connDraft, connMode);
        if (problem) { showError(problem); return; }

        const res = await startCampaign({
          organizationId: activeOrganization.id,
          name,
          campaign: buildCampaignConfig(connDraft, connMode),
          locationRequirements: [{ id: 'connectivity-location', locationId: values.locationId, modemCount: 1 }],
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        const active = (res.modems || []).filter((m) => m.state === 'active').length;
        const refused = (res.modems || []).filter((m) => m.state === 'refused');
        // Name the refusals — a run that silently starts on fewer modems than the location has
        // is the single most confusing outcome here.
        success(refused.length
          ? `Started on ${active} modem(s); ${refused.length} refused (${refused.map((m) => m.refusedCode).join(', ')})`
          : `Started on ${active} modem(s)`);
        onConnectivityStarted?.(res.run.id);
        onClose();
        return;
      }

      // Edit path: update the config in place; don't kick off a run.
      if (isEdit && editingConfig) {
        if (values.configMode !== 'oneOff') {
          throw new Error('Edit form must be in oneOff mode');
        }
        // Same min-length rule as the create path's "Save as" name. The zod
        // refine only fires when saveAs is checked, so enforce here for edit.
        const editedName = (values.saveAsName ?? '').trim();
        if (editedName.length < 3) {
          form.setError('saveAsName', { message: 'Name (min 3 chars) required' });
          return;
        }
        const update = buildConfigPayload(values, locations, clusters);
        // Include flow metadata so the user can change the underlying flow
        // when editing. We resolve from the currently-loaded flows list.
        const flow = flows.find((f) => f.id === values.flowId);
        const flowUpdate = flow
          ? {
              flowId: flow.id,
              flowName: flow.name,
              flowType: (flow.tags?.includes('primitive') ? 'primitive' : 'scenario') as 'primitive' | 'scenario',
              flowVersion: (flow as any).version ?? 1,
            }
          : {};
        // Flow input parameters (dialNumber, apn, …). buildConfigPayload only
        // covers where/when/eSIM/network, so without this the Parameters
        // section rendered the saved values, let the user edit them, and then
        // dropped them on save — the PUT body had no `inputs` key at all, so
        // the backend's `if (inputs !== undefined)` never fired. Only sent
        // when the flow resolved: an unresolvable flow means we don't know its
        // input definitions, and `{}` would wipe the stored values.
        let inputsUpdate: { inputs?: Record<string, any> } = {};
        if (flow) {
          const flowInputDefs = (((flow as any).inputs ?? []) as FlowInputDefinition[]).filter((i) => !i.hidden);
          const finalizedInputs = finalizeFlowInputs(flowInputDefs, (values as any).flowInputs || {});
          const missingInputs = flowInputDefs.filter(
            (i) => i.required && (finalizedInputs[i.name] === undefined || finalizedInputs[i.name] === ''),
          );
          if (missingInputs.length > 0) {
            showError(`Missing required parameter(s): ${missingInputs.map((m) => m.label || m.name).join(', ')}`);
            return;
          }
          inputsUpdate = { inputs: finalizedInputs };
        }
        await updateTestConfiguration(editingConfig.id, activeOrganization.id, {
          name: editedName,
          ...flowUpdate,
          ...update,
          ...inputsUpdate,
        } as any);
        success('Configuration saved');
        onEditSaved?.();
        onClose();
        return;
      }

      // Resolve the saved config snapshot when running an existing one.
      let runtimeConfig: TestConfiguration;
      let savedConfigId: string | undefined;
      let resolvedFlowId: string;
      let resolvedFlowName: string;
      let resolvedFlowType: 'primitive' | 'scenario';
      let resolvedFlowVersion: number;

      if (values.configMode === 'useSaved') {
        const c = configs.find((c) => c.id === values.testConfigurationId);
        if (!c) throw new Error('Saved configuration not found');
        runtimeConfig = c;
        savedConfigId = c.id;
        resolvedFlowId = c.flowId ?? '';
        resolvedFlowName = c.flowName ?? 'Unknown';
        resolvedFlowType = c.flowType ?? 'scenario';
        resolvedFlowVersion = c.flowVersion ?? 1;
      } else {
        // oneOff
        const flow = flows.find((f) => f.id === values.flowId);
        if (!flow) throw new Error('Flow not found');
        resolvedFlowId = flow.id;
        resolvedFlowName = flow.name;
        resolvedFlowType = (flow.tags?.includes('primitive') ? 'primitive' : 'scenario');
        resolvedFlowVersion = (flow as any).version ?? 1;

        const payload = buildConfigPayload(values, locations, clusters);

        // Collect flow input parameters (apn, dialNumber, …) for the selected
        // flow, applying defaults + preset rules. Required missing → block.
        const flowInputDefs = (((flow as any).inputs ?? []) as FlowInputDefinition[]).filter((i) => !i.hidden);
        const finalizedInputs = finalizeFlowInputs(flowInputDefs, (values as any).flowInputs || {});
        const missingInputs = flowInputDefs.filter(
          (i) => i.required && (finalizedInputs[i.name] === undefined || finalizedInputs[i.name] === ''),
        );
        if (missingInputs.length > 0) {
          showError(`Missing required parameter(s): ${missingInputs.map((m) => m.label || m.name).join(', ')}`);
          return;
        }
        const hasInputs = Object.keys(finalizedInputs).length > 0;

        // Recurring configs MUST be persisted — the processTestSchedules cron
        // reads test_configurations, so an unsaved recurring config would never
        // fire. Persist when saving explicitly OR whenever timing is recurring.
        const mustPersist = (values.saveAs && values.saveAsName) || payload.timing.type === 'recurring';

        if (mustPersist) {
          // Persist as a reusable config (backend sets isRecurring/nextFireAt
          // for recurring + persists forcedRat/forcedNetworkMccMnc/timeZone/inputs).
          const created = await createTestConfiguration(activeOrganization.id, currentUser.uid, {
            name: values.saveAsName?.trim() || `${resolvedFlowName} — ${payload.timing.type}`,
            flowId: resolvedFlowId,
            flowName: resolvedFlowName,
            flowType: resolvedFlowType,
            flowVersion: resolvedFlowVersion,
            locationRequirements: payload.locationRequirements,
            timing: payload.timing,
            esim: payload.esim,
            priority: payload.priority,
            useEmulator: payload.useEmulator,
            ...(payload.forcedRat ? { forcedRat: payload.forcedRat } : {}),
            ...(payload.forcedNetworkMccMnc ? { forcedNetworkMccMnc: payload.forcedNetworkMccMnc } : {}),
            ...(payload.timeZone ? { timeZone: payload.timeZone } : {}),
            ...(hasInputs ? { inputs: finalizedInputs } : {}),
          } as any);
          runtimeConfig = created;
          savedConfigId = created.id;
        } else {
          // Inline configuration (not saved). Extras live alongside the
          // typed fields so dispatchRunFromConfig + the backend can read them.
          runtimeConfig = {
            id: 'inline',
            organizationId: activeOrganization.id,
            name: 'Inline Configuration',
            flowId: resolvedFlowId,
            flowName: resolvedFlowName,
            flowType: resolvedFlowType,
            flowVersion: resolvedFlowVersion,
            locationRequirements: payload.locationRequirements,
            timing: payload.timing,
            esim: payload.esim,
            priority: payload.priority,
            useEmulator: payload.useEmulator,
            createdBy: currentUser.uid,
            createdAt: new Date(),
            updatedAt: new Date(),
            usageCount: 0,
            ...(payload.forcedRat ? { forcedRat: payload.forcedRat } : {}),
            ...(payload.forcedNetworkMccMnc ? { forcedNetworkMccMnc: payload.forcedNetworkMccMnc } : {}),
            ...(payload.timeZone ? { timeZone: payload.timeZone } : {}),
            ...(hasInputs ? { inputs: finalizedInputs } : {}),
          } as TestConfiguration;
        }
      }

      // Recurring: the persisted test_configuration IS the schedule — the
      // processTestSchedules cron fires it on its cadence. Never run-now, and
      // don't create a one-off test_run row.
      if (runtimeConfig.timing.type === 'recurring') {
        success('Recurring schedule created — it will run on its cadence.');
        onClose();
        return;
      }

      // Scheduled?
      const scheduledFor =
        runtimeConfig.timing.type === 'scheduled' && runtimeConfig.timing.executeAt
          ? (runtimeConfig.timing.executeAt instanceof Date
              ? runtimeConfig.timing.executeAt
              : new Date(runtimeConfig.timing.executeAt))
          : undefined;

      // Create the test_run row. Backend syncs status/executionId via testRunId.
      const run = await createTestRun(activeOrganization.id, currentUser.uid, {
        flowId: resolvedFlowId,
        flowName: resolvedFlowName,
        flowType: resolvedFlowType,
        flowVersion: resolvedFlowVersion,
        testConfigurationId: savedConfigId,
        configuration: runtimeConfig,
        scheduledFor,
      });

      // Schedule path: don't kick off now; backend cron picks it up.
      if (scheduledFor) {
        success(`Test scheduled for ${scheduledFor.toLocaleString()}`);
        onRunStarted?.(run.id);
        onClose();
        return;
      }

      // Run-now path: fire-and-forget. Backend handles all test_runs syncs.
      const locReq = runtimeConfig.locationRequirements?.[0];
      const locationId = locReq?.locationId;
      const clusterId = locReq?.clusterId;
      // ONE config → execution-context mapping shared with the backend
      // scheduler and dispatchRunFromConfig (#209: a binding present on one
      // path and dropped on another). Carries eSIM / group / RAT / PLMN / pin.
      const executionContext = executionContextFromConfig(runtimeConfig as any);

      // Need the flow definition for the runtime call.
      const flowDef = flows.find((f) => f.id === resolvedFlowId);
      if (!flowDef) throw new Error('Flow definition not loaded');

      // Await the submit so pre-flight failures (NO_CAPABLE_MODEM 409 from
      // structurally impossible constraints) surface as the user's only
      // feedback instead of racing a "started" toast that lies. Backend
      // returns 202 in <500ms.
      const submit = (runId: string, ctx: typeof executionContext) =>
        runtimeService.executeFlow(
          flowDef,
          { clusterId, ...((runtimeConfig as any).inputs || {}) },
          currentUser.uid,
          activeOrganization.id,
          ctx,
          locationId,
          runtimeConfig.useEmulator,
          runId, // testRunId — backend links + syncs the test_run
        );
      let startedRunId = run.id;
      try {
        await submit(run.id, executionContext);
      } catch (err: any) {
        // #375: the only blocker is consent to move a limited-download eSIM.
        // Ask, then start an approved run (the refused one stays in history).
        if (isEsimMoveApprovalError(err) && await confirmMove(err.message)) {
          try {
            const approvedConfig = {
              ...runtimeConfig,
              esim: { ...runtimeConfig.esim, allowRelocation: true },
            } as TestConfiguration;
            const approved = await createTestRun(activeOrganization.id, currentUser.uid, {
              flowId: resolvedFlowId,
              flowName: resolvedFlowName,
              flowType: resolvedFlowType,
              flowVersion: resolvedFlowVersion,
              testConfigurationId: savedConfigId,
              configuration: approvedConfig,
            });
            await submit(approved.id, { ...executionContext, allowEsimRelocation: true });
            startedRunId = approved.id;
          } catch (retryErr: any) {
            console.error('[ExecutionDialog] approved executeFlow submit error:', retryErr);
            showError(retryErr?.message || 'Failed to start test run');
            return;
          }
        } else if (isEsimMoveApprovalError(err)) {
          info(ESIM_MOVE_DECLINED);
          return; // Keep the dialog open so the user can adjust
        } else {
          console.error('[ExecutionDialog] executeFlow submit error:', err);
          showError(err?.message || 'Failed to start test run');
          return; // Keep the dialog open so the user can adjust
        }
      }

      success('Test execution started');
      onRunStarted?.(startedRunId);
      onClose();
    } catch (err: any) {
      console.error('[ExecutionDialog] submit error:', err);
      showError(err?.message || 'Failed to start test run');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Derived UI bits ─────────────────────────────────────────────────────
  // #375: offer the move approval only when a move would spend a limited
  // download (unlimited eSIMs move freely; shared/locked/spent never move).
  const limitedEsimSelected = (() => {
    const w = watched as any;
    const candidates =
      w.esimMode === 'single' ? esimProfiles.filter((p) => p.id === w.esimProfileId)
      : w.esimMode === 'batch' ? esimProfiles.filter((p) => (p as any).groupId === w.esimGroupId)
      : [];
    return candidates.some((p) => esimRelocationPolicy(p as any) === 'needs_approval');
  })();
  const selectedFlow = flows.find((f) => f.id === flowId);
  const selectedConfig = configs.find((c) => c.id === (watched as any).testConfigurationId);
  // #283 — a connectivity run replaces eSIM / network constraints / parameters / timing with
  // its own fields, because none of those apply to something that holds a registration.
  const connectivityMode = parseConnectivityMode(flowId);
  const titleText = isEdit ? 'Edit Configuration' : 'New Test Run';
  const descText = isEdit
    ? 'Update this saved configuration.'
    : connectivityMode
      ? 'Holds every private-edge modem at the location for as long as the run lasts. It does not consume tokens, and other tests keep running on what it sets up.'
      : 'Pick a flow, set where + when, then launch it. Optionally save the setup for reuse.';

  return (
    <>
    {esimMoveDialog}
    <Sheet open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl p-0 flex flex-col"
        onInteractOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>{titleText}</SheetTitle>
          <SheetDescription>{descText}</SheetDescription>
        </SheetHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
            </div>
          ) : (
            <>
              {/* ── Name (edit mode, and connectivity runs, which are always named) ── */}
              {(isEdit || !!connectivityMode) && (
                <Section title="Name" icon={Tag} required>
                  <Input
                    placeholder={connectivityMode ? 'e.g. Overnight attach watch — week 38' : 'Configuration name'}
                    value={(watched as any).saveAsName || ''}
                    onChange={(e) => form.setValue('saveAsName', e.target.value as any, { shouldValidate: true })}
                  />
                  {form.formState.errors.saveAsName && (
                    <FieldError message={form.formState.errors.saveAsName.message as string} />
                  )}
                </Section>
              )}

              {/* ── Flow ───────────────────────────────────────────── */}
              <Section title="Flow" icon={Workflow} required>
                <FlowPicker
                  flows={pickableFlows}
                  value={configMode === 'useSaved' ? (selectedConfig?.flowId ?? '') : flowId}
                  onChange={(id) => {
                    const flow = flows.find((f) => f.id === id);
                    form.setValue('flowId', id, { shouldValidate: true });
                    // A connectivity run takes exactly one location, and the cluster tab is
                    // hidden for it — so a cluster chosen before the switch must not linger as
                    // the only thing standing in for a location.
                    if (parseConnectivityMode(id)) {
                      form.setValue('locationMode', 'single' as any);
                      form.setValue('clusterId', undefined as any);
                    }
                    // When editing, also keep the cached flow metadata fields in sync
                    // (the backend update path writes them through).
                    if (isEdit && flow) {
                      // Stored on form via shadow setValue — these aren't in the schema
                      // but the submit handler reads them via flows.find().
                    }
                  }}
                  disabled={configMode === 'useSaved'}
                />
                {selectedFlow && configMode === 'useSaved' && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Flow is inherited from the saved configuration below.
                  </p>
                )}
                {form.formState.errors.flowId && (
                  <FieldError message={form.formState.errors.flowId.message as string} />
                )}
              </Section>

              {/* ── Configuration mode ─────────────────────────────── */}
              {!isEdit && !connectivityMode && (
                <Section title="Configuration" icon={Layers}>
                  <RadioGroup
                    value={configMode}
                    onValueChange={(v) => {
                      // Switching modes: reset the form to the chosen mode's defaults
                      // so all the conditional fields start clean.
                      if (v === 'oneOff') {
                        const base = defaultsForCreate();
                        if (selectedFlow) base.flowId = selectedFlow.id;
                        form.reset(base);
                      } else if (v === 'useSaved') {
                        form.reset(defaultsForUseSaved('', ''));
                      }
                    }}
                    className="space-y-2"
                  >
                    <div className="flex items-start gap-2 p-3 rounded border bg-card">
                      <RadioGroupItem value="oneOff" id="cfg-oneoff" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="cfg-oneoff" className="cursor-pointer font-medium">
                          One-off
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Configure inline. Optionally save for reuse.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 rounded border bg-card">
                      <RadioGroupItem value="useSaved" id="cfg-saved" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="cfg-saved" className="cursor-pointer font-medium">
                          Use a saved configuration
                        </Label>
                        {configMode === 'useSaved' && (
                          <div className="mt-2 space-y-2">
                            <Select
                              value={(watched as any).testConfigurationId || ''}
                              onValueChange={(v) => {
                                const c = configs.find((c) => c.id === v);
                                form.setValue('testConfigurationId', v, { shouldValidate: true });
                                if (c?.flowId) form.setValue('flowId', c.flowId, { shouldValidate: true });
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Pick a saved configuration…" />
                              </SelectTrigger>
                              <SelectContent>
                                {configs.length === 0 ? (
                                  <div className="px-2 py-3 text-sm text-muted-foreground">
                                    No saved configurations yet.
                                  </div>
                                ) : configs.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {selectedConfig && <SavedConfigSummary config={selectedConfig} locations={locations} clusters={clusters} />}
                          </div>
                        )}
                      </div>
                    </div>
                  </RadioGroup>
                </Section>
              )}

              {/* ── One-off body ───────────────────────────────────── */}
              {(isEdit || configMode === 'oneOff' || !!connectivityMode) && (
                <>
                  <Section title="Where" icon={MapPin} required>
                    <LocationPicker
                      mode={(watched as any).locationMode || 'single'}
                      locationId={(watched as any).locationId}
                      clusterId={(watched as any).clusterId}
                      locations={locations}
                      clusters={clusters}
                      onModeChange={(m) => {
                        form.setValue('locationMode', m as any);
                        form.setValue('locationId', undefined as any);
                        form.setValue('clusterId', undefined as any);
                      }}
                      onLocationChange={(id) => {
                        form.setValue('locationId', id as any, { shouldValidate: true });
                        // A pin belongs to one location — clear it on change.
                        form.setValue('pinDeviceId', undefined as any);
                        form.setValue('pinModemImei', undefined as any);
                      }}
                      onClusterChange={(id) => form.setValue('clusterId', id as any, { shouldValidate: true })}
                      privateOnly={!!connectivityMode}
                    />
                    {!connectivityMode && ((watched as any).locationMode || 'single') === 'single' && (watched as any).locationId && (
                      <ModemPinPicker
                        locationId={(watched as any).locationId}
                        pinDeviceId={(watched as any).pinDeviceId}
                        pinModemImei={(watched as any).pinModemImei}
                        onChange={(pin) => {
                          form.setValue('pinDeviceId', (pin?.deviceId ?? undefined) as any);
                          form.setValue('pinModemImei', (pin?.modemImei ?? undefined) as any);
                        }}
                      />
                    )}
                    {connectivityMode && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Only your own sites are listed — this can only run on edge hardware your
                        organization owns. Every private-edge modem there joins; one that refuses
                        (wrong model, device uplink, already held by another run) is reported and
                        skipped.
                      </p>
                    )}
                    {form.formState.errors.locationId && <FieldError message={form.formState.errors.locationId.message as string} />}
                    {form.formState.errors.clusterId && <FieldError message={form.formState.errors.clusterId.message as string} />}
                  </Section>

                  {connectivityMode && (
                    <Section title={connectivityMode === 'hunt' ? 'PLMN hunt' : 'Persistent attach'} icon={Radio} required>
                      <ConnectivityFields
                        mode={connectivityMode}
                        draft={connDraft}
                        onChange={setConnDraft}
                        organizationId={activeOrganization?.id || ''}
                      />
                    </Section>
                  )}

                  {!connectivityMode && (
                  <>
                  <Section title="eSIM" icon={Wifi}>
                    <ESimPicker
                      mode={(watched as any).esimMode || 'auto'}
                      profileId={(watched as any).esimProfileId}
                      groupId={(watched as any).esimGroupId}
                      profiles={esimProfiles}
                      groups={esimGroups}
                      sharedGroups={sharedGroupsForOrg(esimProfiles, activeOrganization)}
                      locationId={
                        (watched as any).locationMode === 'cluster'
                          ? undefined
                          : (watched as any).locationId
                      }
                      locations={locations}
                      deviceLocations={deviceLocations}
                      onChange={(next) => {
                        form.setValue('esimMode', next.esimMode as any);
                        form.setValue('esimProfileId', next.esimProfileId as any, { shouldValidate: true });
                        form.setValue('esimGroupId', next.esimGroupId as any, { shouldValidate: true });
                      }}
                    />
                    {form.formState.errors.esimProfileId && <FieldError message={form.formState.errors.esimProfileId.message as string} />}
                    {form.formState.errors.esimGroupId && <FieldError message={form.formState.errors.esimGroupId.message as string} />}
                    {limitedEsimSelected && (
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="esim-allow-relocation"
                          checked={!!(watched as any).esimAllowRelocation}
                          onCheckedChange={(v) => form.setValue('esimAllowRelocation', !!v as any)}
                          className="mt-0.5"
                        />
                        <Label htmlFor="esim-allow-relocation" className="cursor-pointer text-sm leading-snug">
                          Allow moving this eSIM between modems
                          <span className="block text-xs text-muted-foreground">
                            When the modem holding it can't run this test, the eSIM is deleted there and downloaded
                            onto one that can. Each move uses one of its limited downloads. Needed for scheduled runs;
                            a run started now asks first.
                          </span>
                        </Label>
                      </div>
                    )}
                    {/* "Auto" reads like a filled-in value, so testers don't
                        realize no SIM is bound to the run — make the
                        consequence explicit (user report 2026-08-17). */}
                    {((watched as any).esimMode === 'auto' || (watched as any).esimMode === 'none' || !(watched as any).esimMode) && (
                      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          No eSIM is bound to this run — it uses whichever SIM is already on the
                          allocated modem, and fails if the modems there have none.
                        </span>
                      </p>
                    )}
                  </Section>

                  <CollapsibleSection
                    title="Network constraints"
                    icon={Signal}
                    summary={summariseNetwork(watched)}
                    defaultOpen={!!(watched as any).forcedRat && (watched as any).forcedRat !== 'auto'}
                  >
                    <div className="space-y-3">
                      <RatSelector
                        value={(watched as any).forcedRat || 'auto'}
                        onChange={(rat) => form.setValue('forcedRat', rat as any)}
                        availableRats={null}
                      />
                      <div>
                        <Label htmlFor="plmn" className="text-sm text-muted-foreground">
                          Force operator (PLMN MCCMNC, optional)
                        </Label>
                        <Input
                          id="plmn"
                          placeholder="e.g. 42501"
                          maxLength={6}
                          value={(watched as any).forcedNetworkMccMnc || ''}
                          onChange={(e) => form.setValue('forcedNetworkMccMnc', e.target.value || undefined as any)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </CollapsibleSection>

                  {(() => {
                    const defs = (((selectedFlow as any)?.inputs ?? []) as FlowInputDefinition[]).filter((i) => !i.hidden);
                    if (defs.length === 0) return null;
                    const vals = (watched as any).flowInputs || {};
                    const setInput = (name: string, value: string, type: string) => {
                      const cur = { ...((form.getValues() as any).flowInputs || {}) };
                      cur[name] = type === 'number' ? (value === '' ? '' : Number(value)) : value;
                      form.setValue('flowInputs', cur as any);
                    };
                    return (
                      <CollapsibleSection
                        title="Parameters"
                        icon={Settings2}
                        summary={defs.map((d) => d.label || d.name).join(', ')}
                        // Open when something is required OR when the flow takes
                        // an eSIM-resolved input (apn) — the APN override is the
                        // field users go looking for (issue #161), and collapsed
                        // it reads as missing.
                        defaultOpen={defs.some((d) => d.required || ESIM_RESOLVED_INPUTS.has(d.name))}
                      >
                        <div className="space-y-4">
                          <FlowInputFields inputs={defs} values={vals} onChange={setInput} />
                        </div>
                      </CollapsibleSection>
                    );
                  })()}

                  <CollapsibleSection
                    title="Timing"
                    icon={Clock}
                    summary={summariseTiming(watched)}
                    defaultOpen={(watched as any).timing !== 'now'}
                  >
                    <TimingPicker
                      timing={(watched as any).timing || 'now'}
                      executeAt={(watched as any).executeAt}
                      recurrence={(watched as any).recurrence}
                      timeZone={(watched as any).timeZone}
                      onTimingChange={(t) => form.setValue('timing', t as any)}
                      onExecuteAtChange={(d) => form.setValue('executeAt', d as any, { shouldValidate: true })}
                      onRecurrenceChange={(r) => form.setValue('recurrence', r as any, { shouldValidate: true })}
                      onTimeZoneChange={(tz) => form.setValue('timeZone', tz as any)}
                    />
                    {form.formState.errors.executeAt && <FieldError message={form.formState.errors.executeAt.message as string} />}
                  </CollapsibleSection>
                  </>
                  )}

                  {!isEdit && !connectivityMode && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="save-as"
                          checked={!!(watched as any).saveAs}
                          onCheckedChange={(v) => form.setValue('saveAs', !!v as any)}
                        />
                        <Label htmlFor="save-as" className="cursor-pointer text-sm flex items-center gap-1.5">
                          <Save className="w-3.5 h-3.5 text-muted-foreground" />
                          Save as a configuration I can re-run later
                        </Label>
                      </div>
                      {(watched as any).saveAs && (
                        <Input
                          placeholder="Name this configuration (e.g. Berlin 5G nightly)"
                          value={(watched as any).saveAsName || ''}
                          onChange={(e) => form.setValue('saveAsName', e.target.value as any, { shouldValidate: true })}
                          className="mt-2"
                        />
                      )}
                      {form.formState.errors.saveAsName && <FieldError message={form.formState.errors.saveAsName.message as string} />}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </form>

        <div className="border-t px-6 py-4 flex items-center justify-between gap-2 bg-background">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={submitting || loading}
            icon={submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          >
            {submitting ? 'Working…' : submitLabel(isEdit, watched)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function submitLabel(isEdit: boolean, v: any): string {
  if (isEdit) return 'Save Changes';
  // A connectivity run is started and later stopped, never "run now" — and it is never saved
  // as a re-runnable configuration, because starting it twice would fight over the modems.
  if (parseConnectivityMode(v.flowId)) return 'Start';
  if (v.configMode === 'useSaved') return 'Run Now';
  // Consistent across Scheduled / Recurring: "Save & Schedule" iff saveAs,
  // otherwise just "Schedule". Same shape as the Run-now pair below
  // ("Save & Run" / "Run Now"). The previous code asymmetrically appended
  // "Test" or "Recurring" or ignored saveAs entirely.
  if (v.timing === 'scheduled' || v.timing === 'recurring') {
    return v.saveAs ? 'Save & Schedule' : 'Schedule';
  }
  return v.saveAs ? 'Save & Run' : 'Run Now';
}

function buildConfigPayload(
  v: RunTestFormValues,
  _locations: Location[],
  _clusters: Cluster[],
): {
  locationRequirements: LocationRequirement[];
  timing: ExecutionTiming;
  esim: ESimSelectionConfig;
  priority: number;
  useEmulator: boolean;
  forcedRat?: ForcedRat;
  forcedNetworkMccMnc?: string;
  /** IANA TZ for interpreting Scheduled.executeAt / Recurring.startDate as
   *  wall-clock-in-zone. Persisted as a top-level field on the saved config
   *  doc (not in shared-types yet). Backend cron will resolve to UTC. */
  timeZone?: string;
} {
  const locReq: LocationRequirement = {
    id: `loc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    modemCount: 1,
    ...(v.locationMode === 'single' ? { locationId: v.locationId } : { clusterId: v.clusterId }),
    // Modem pin (#252) — single-location only.
    ...(v.locationMode === 'single' && v.pinModemImei
      ? { deviceId: v.pinDeviceId, modemImei: v.pinModemImei }
      : {}),
  };

  const timing: ExecutionTiming = {
    type: v.timing === 'now' ? 'now' : v.timing === 'scheduled' ? 'scheduled' : 'recurring',
    ...(v.timing === 'scheduled' && v.executeAt ? { executeAt: v.executeAt } : {}),
    ...(v.timing === 'recurring' && v.recurrence ? {
      recurrence: {
        interval: v.recurrence.interval,
        unit: v.recurrence.unit,
        ...(v.recurrence.unit === 'pass_windows' && v.recurrence.offsetSec != null ? { offsetSec: v.recurrence.offsetSec } : {}),
        ...(v.recurrence.startDate ? { startDate: v.recurrence.startDate } : {}),
        ...(v.recurrence.endDate ? { endDate: v.recurrence.endDate } : {}),
        ...(v.recurrence.cronExpression ? { cronExpression: v.recurrence.cronExpression } : {}),
      },
    } : {}),
  };

  const esim: ESimSelectionConfig = (() => {
    if (v.esimMode === 'none' || v.esimMode === 'auto') return { mode: 'none' };
    const move = v.esimAllowRelocation ? { allowRelocation: true } : {};
    if (v.esimMode === 'single') return { mode: 'single', profileId: v.esimProfileId, ...move };
    return { mode: 'batch', batch: { type: 'group', groupId: v.esimGroupId }, ...move };
  })();

  return {
    locationRequirements: [locReq],
    timing,
    esim,
    priority: 5,
    useEmulator: false,
    forcedRat: v.forcedRat as ForcedRat | undefined,
    forcedNetworkMccMnc: v.forcedNetworkMccMnc,
    timeZone: v.timeZone,
  };
}

function summariseNetwork(v: any): string {
  const rat = v.forcedRat && v.forcedRat !== 'auto' ? v.forcedRat.toUpperCase() : 'Auto';
  const plmn = v.forcedNetworkMccMnc ? ` · PLMN ${v.forcedNetworkMccMnc}` : '';
  return `${rat}${plmn}`;
}

function summariseTiming(v: any): string {
  if (v.timing === 'scheduled' && v.executeAt) return `Scheduled · ${new Date(v.executeAt).toLocaleString()}`;
  if (v.timing === 'recurring' && v.recurrence) return `Every ${v.recurrence.interval} ${v.recurrence.unit}`;
  return 'Run now';
}

// ── Tiny presentational primitives ────────────────────────────────────────

function Section({
  title, icon: Icon, required, children,
}: { title: string; icon?: any; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="w-4 h-4 text-muted-foreground" /> : null}
        <Label className="text-sm font-medium">{title}</Label>
        {required && <span className="text-status-error">*</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function CollapsibleSection({
  title, icon: Icon, summary, defaultOpen, children,
}: { title: string; icon?: any; summary?: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 rounded border bg-card hover:bg-muted transition-colors">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-muted-foreground" /> : null}
          <span className="text-sm font-medium">{title}</span>
          {summary && <Badge variant="secondary" className="text-xs">{summary}</Badge>}
        </div>
        <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3 pb-1 px-1">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-status-error mt-1">
      <AlertCircle className="w-3 h-3" />
      <span>{message}</span>
    </div>
  );
}

// ── Flow picker (cmdk inside Popover, grouped) ────────────────────────────

function FlowPicker({
  flows, value, onChange, disabled,
}: { flows: Flow[]; value?: string; onChange: (id: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const selected = flows.find((f) => f.id === value);
  // A connectivity run has no flow document, so it can't come from `flows` — it is offered as
  // its own group here rather than behind a second button, because it is a test and users
  // should find it where they look for every other test (#283).
  //
  // Group order is deliberate: the org's OWN scenarios first (what a tenant came here for),
  // then the quick tests they run most, then connectivity, then primitives. Connectivity led
  // the list when it was new, which put the rarest, most side-effectful option under the
  // cursor of everyone opening the picker.
  const connectivity = CONNECTIVITY_OPTIONS.find((o) => o.id === value);
  // Quick tests are the system sys_quick_* flows; show them in their own group.
  const isQuickTest = (f: Flow) => f.id.startsWith('sys_quick_');
  const quickTests = flows.filter(isQuickTest);
  const primitives = flows.filter((f) => !isQuickTest(f) && f.tags?.includes('primitive'));
  const scenarios = flows.filter((f) => !isQuickTest(f) && !f.tags?.includes('primitive'));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selected ? selected.name
              : connectivity ? connectivity.name
              : (disabled ? 'No flow selected' : 'Pick a flow…')}
          </span>
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]">
        <Command>
          <CommandInput placeholder="Search flows…" />
          <CommandList>
            <CommandEmpty>No flows found.</CommandEmpty>
            {scenarios.length > 0 && (
              <CommandGroup heading="Scenarios">
                {scenarios.map((f) => (
                  <FlowOption key={f.id} flow={f} selected={f.id === value} onSelect={() => { onChange(f.id); setOpen(false); }} />
                ))}
              </CommandGroup>
            )}
            {quickTests.length > 0 && (
              <CommandGroup heading="Quick Tests">
                {quickTests.map((f) => (
                  <FlowOption key={f.id} flow={f} selected={f.id === value} onSelect={() => { onChange(f.id); setOpen(false); }} />
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading="Connectivity Tests">
              {CONNECTIVITY_OPTIONS.map((o) => (
                <CommandItem
                  key={o.id}
                  value={`${o.name} ${o.hint}`}
                  onSelect={() => { onChange(o.id); setOpen(false); }}
                  className="flex items-center gap-2"
                >
                  <Radio className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">
                    {o.name}
                    <span className="text-muted-foreground"> — {o.hint}</span>
                  </span>
                  {o.id === value && <Check className="w-4 h-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {primitives.length > 0 && (
              <CommandGroup heading="Primitives">
                {primitives.map((f) => (
                  <FlowOption key={f.id} flow={f} selected={f.id === value} onSelect={() => { onChange(f.id); setOpen(false); }} />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FlowOption({ flow, selected, onSelect }: { flow: Flow; selected: boolean; onSelect: () => void }) {
  const isQuickTest = flow.id.startsWith('sys_quick_');
  const isPrimitive = flow.tags?.includes('primitive');
  const Icon = isQuickTest ? Zap : isPrimitive ? FileCode2 : Layers;
  return (
    <CommandItem value={flow.name} onSelect={onSelect} className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <span className="flex-1 truncate">{flow.name}</span>
      {selected && <Check className="w-4 h-4" />}
    </CommandItem>
  );
}

// ── Location picker (Tabs Single/Cluster + Select) ────────────────────────

type RecurrenceUnitUi = 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'pass_windows';

function LocationPicker({
  mode, locationId, clusterId, locations, clusters,
  onModeChange, onLocationChange, onClusterChange, privateOnly = false,
}: {
  mode: 'single' | 'cluster';
  locationId?: string; clusterId?: string;
  locations: Location[]; clusters: Cluster[];
  onModeChange: (m: 'single' | 'cluster') => void;
  onLocationChange: (id: string) => void;
  onClusterChange: (id: string) => void;
  /** Connectivity runs are private-edge only, and take exactly one location (#283). */
  privateOnly?: boolean;
}) {
  return (
    <div className="space-y-3">
      {!privateOnly && (
        <Tabs value={mode} onValueChange={(v) => onModeChange(v as 'single' | 'cluster')}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="single"><MapPin className="w-3.5 h-3.5 mr-1.5" />Single location</TabsTrigger>
            <TabsTrigger value="cluster"><Layers className="w-3.5 h-3.5 mr-1.5" />Cluster</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {privateOnly || mode === 'single' ? (
        // Use the shared hierarchical picker (Region → Country → Location with
        // search + flags + modem-count badges) — same component the schedule
        // form and admin pages use. Replaces the previous flat alphabetical
        // city list.
        <HierarchicalLocationSelector
          mode="single"
          selectedLocationId={locationId || null}
          onSelectLocation={(id) => onLocationChange(id || '')}
          placeholder={privateOnly ? 'Search your sites…' : 'Search locations…'}
          onlyMine={privateOnly}
        />
      ) : (
        <Select value={clusterId || ''} onValueChange={onClusterChange}>
          <SelectTrigger><SelectValue placeholder="Pick a cluster…" /></SelectTrigger>
          <SelectContent>
            {clusters.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">
                No clusters defined.{' '}
                <a href="/clusters" className="font-medium text-primary hover:underline">
                  Create one on the Clusters page
                </a>
                .
              </div>
            ) : clusters.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ── eSIM picker ───────────────────────────────────────────────────────────
//
// Shares `ESimSelectionCombobox` with Quick Tests so both surfaces search the
// same way and apply the same residency rules. The wizard's `esimMode` triple
// (`auto|single|batch|none` + profileId + groupId) is kept as the persisted
// shape — only the control changed — via the adapters below.
//
// The old 4-way ToggleGroup collapsed here because `auto` and `none` produce
// the SAME saved config (`toConfig`: both → `{mode:'none'}`), so they were two
// labels for one outcome; the combobox offers that outcome once, as its
// clear row. `Specific` vs `Batch` is now inferred from WHAT you picked (a SIM
// vs a group) instead of being a mode you set before you can pick.

/** Form triple → the combobox's encoded value. */
function esimFormToSelection(v: {
  esimMode?: string; esimProfileId?: string; esimGroupId?: string;
}): string | null {
  if (v.esimMode === 'single' && v.esimProfileId) {
    // A shared pool is stored as a bare sentinel in profileId (pre-existing).
    return parseSharedPoolSentinel(v.esimProfileId).isPool
      ? `${SHARED_PREFIX}${v.esimProfileId}`
      : encodeProfileSelection(v.esimProfileId);
  }
  if (v.esimMode === 'batch' && v.esimGroupId) {
    // Older configs could hold a shared pool here too — both forms resolve
    // server-side (`QueueService` checks poolFromProfile AND poolFromGroup).
    return parseSharedPoolSentinel(v.esimGroupId).isPool
      ? `${SHARED_PREFIX}${v.esimGroupId}`
      : v.esimGroupId;
  }
  return null;
}

/**
 * Combobox value → the form triple.
 *
 * `prev` is the form's current triple, used only to keep a shared pool in the
 * slot a saved config already stored it in. A pool is legal as either
 * `esimProfileId` or `esimGroupId` and resolves identically at dispatch
 * (`QueueService` checks `poolFromProfile` AND `poolFromGroup`), so without
 * this, merely opening and re-saving a `batch`-stored pool config would
 * rewrite it to `single` — a silent shape change that also flips the /tests
 * "Batch" column to "Single".
 */
function esimSelectionToForm(
  value: string | null,
  prev?: { esimMode?: string; esimGroupId?: string },
): {
  esimMode: 'auto' | 'single' | 'batch';
  esimProfileId?: string;
  esimGroupId?: string;
} {
  if (!value) return { esimMode: 'auto', esimProfileId: undefined, esimGroupId: undefined };
  const parsed = parseESimSelection(value);
  if (parsed.groupId) {
    return { esimMode: 'batch', esimGroupId: parsed.groupId, esimProfileId: undefined };
  }
  if (parsed.isSharedPool && prev?.esimMode === 'batch' && prev.esimGroupId === parsed.profileId) {
    return { esimMode: 'batch', esimGroupId: parsed.profileId || undefined, esimProfileId: undefined };
  }
  // `profile:<id>` and `shared:<sentinel>` both travel as eSimProfileId.
  return { esimMode: 'single', esimProfileId: parsed.profileId || undefined, esimGroupId: undefined };
}

function ESimPicker({
  mode, profileId, groupId, profiles, groups, sharedGroups = [],
  locationId, locations, deviceLocations, onChange,
}: {
  mode: 'auto' | 'single' | 'batch' | 'none';
  profileId?: string; groupId?: string;
  profiles: ESimProfile[]; groups: ESimGroup[];
  sharedGroups?: { groupId: string; groupName: string }[];
  /** Selected location, when the run targets one (cluster mode → undefined). */
  locationId?: string;
  locations: Location[];
  deviceLocations: Map<string, string | null>;
  onChange: (next: ReturnType<typeof esimSelectionToForm>) => void;
}) {
  const residencyOf = (profile: ESimProfile) => {
    const deviceId = profile.installedOn?.deviceId;
    if (!deviceId) return null;
    const locId = deviceLocations.get(deviceId) ?? null;
    return {
      locationId: locId,
      locationName: (locId && locations.find((l) => l.id === locId)?.name) || null,
    };
  };

  // Cluster runs span many locations, so there's nothing single to judge
  // relevance against — leave the list unscoped rather than guess.
  const selectedLocationIds = locationId ? [locationId] : [];

  return (
    <ESimSelectionCombobox
      value={esimFormToSelection({ esimMode: mode, esimProfileId: profileId, esimGroupId: groupId })}
      onChange={(v) => onChange(esimSelectionToForm(v, { esimMode: mode, esimGroupId: groupId }))}
      groups={groups}
      profiles={profiles}
      sharedGroups={sharedGroups}
      sharedProfiles={profiles.filter((p) => p.shared)}
      selectedLocationIds={selectedLocationIds}
      selectedLocationLabel={
        locationId ? locations.find((l) => l.id === locationId)?.name || null : null
      }
      residencyOf={residencyOf}
      clearable
      clearLabel="Auto — whichever SIM is on the modem"
      clearDescription="No eSIM is provisioned or switched for this run"
      profileNote={(profile) => {
        const residency = residencyOf(profile);
        if (!residency) return isProfileReinstallable(profile) ? null : 'code spent';
        return residency.locationName ? `at ${residency.locationName}` : 'installed';
      }}
    />
  );
}

// ── Timing picker (RadioGroup + conditional inputs) ───────────────────────

function TimingPicker({
  timing, executeAt, recurrence, timeZone,
  onTimingChange, onExecuteAtChange, onRecurrenceChange, onTimeZoneChange,
}: {
  timing: 'now' | 'scheduled' | 'recurring';
  executeAt?: Date;
  recurrence?: { interval: number; unit: RecurrenceUnitUi; cronExpression?: string };
  timeZone?: string;
  onTimingChange: (t: 'now' | 'scheduled' | 'recurring') => void;
  onExecuteAtChange: (d: Date | undefined) => void;
  onRecurrenceChange: (r: any) => void;
  onTimeZoneChange: (tz: string) => void;
}) {
  const browserTz = (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined) || 'UTC';
  const effectiveTz = timeZone || browserTz;
  return (
    <div className="space-y-3">
      <RadioGroup value={timing} onValueChange={(v) => onTimingChange(v as any)} className="flex gap-3">
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="now" id="t-now" /><Label htmlFor="t-now" className="cursor-pointer text-sm">Run now</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="scheduled" id="t-sched" /><Label htmlFor="t-sched" className="cursor-pointer text-sm">Schedule</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="recurring" id="t-rec" /><Label htmlFor="t-rec" className="cursor-pointer text-sm">Recurring</Label>
        </div>
      </RadioGroup>

      {timing === 'scheduled' && (
        <ScheduledAtPicker
          executeAt={executeAt}
          timeZone={effectiveTz}
          onChange={onExecuteAtChange}
          onTimeZoneChange={onTimeZoneChange}
        />
      )}

      {timing === 'recurring' && (
        <RecurrenceEditor
          recurrence={recurrence}
          timeZone={effectiveTz}
          onChange={onRecurrenceChange}
          onTimeZoneChange={onTimeZoneChange}
        />
      )}
    </div>
  );
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalTimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const DAYS_OF_WEEK: { value: number; short: string; label: string }[] = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
];

/**
 * Recurrence editor with unit-aware "when" picker.
 *
 *   - minutes / hours        → just interval + unit
 *   - days                   → interval + time-of-day
 *   - weeks                  → interval + day-of-week + time-of-day
 *   - months                 → interval + day-of-month (1-31) + time-of-day
 *
 * The "when" inputs encode into the recurrence's `startDate`. The backend
 * (or downstream cron projection) reads startDate as the anchor and rolls
 * subsequent firings from there.
 */
function RecurrenceEditor({
  recurrence, timeZone, onChange, onTimeZoneChange,
}: {
  recurrence?: { interval: number; unit: RecurrenceUnitUi; startDate?: Date; cronExpression?: string };
  timeZone: string;
  onChange: (r: any) => void;
  onTimeZoneChange: (tz: string) => void;
}) {
  const interval = recurrence?.interval ?? 1;
  const unit = recurrence?.unit ?? 'hours';
  const anchor = recurrence?.startDate ? new Date(recurrence.startDate) : nextSensibleAnchor(unit);

  const update = (patch: Partial<NonNullable<typeof recurrence>>) => {
    onChange({ interval, unit, startDate: anchor, ...recurrence, ...patch });
  };

  const setTime = (timeStr: string) => {
    if (!timeStr) return;
    const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10));
    const next = new Date(anchor);
    next.setHours(Number.isFinite(h) ? h : 9, Number.isFinite(m) ? m : 0, 0, 0);
    update({ startDate: next });
  };

  const setDayOfWeek = (dow: number) => {
    const next = new Date(anchor);
    const diff = dow - next.getDay();
    next.setDate(next.getDate() + diff);
    update({ startDate: next });
  };

  const setDayOfMonth = (dom: number) => {
    const next = new Date(anchor);
    next.setDate(Math.max(1, Math.min(31, dom)));
    update({ startDate: next });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Every</span>
        <Input
          type="number"
          min={1}
          value={interval}
          onChange={(e) => update({ interval: parseInt(e.target.value, 10) || 1 })}
          className="w-20"
        />
        <Select
          value={unit}
          onValueChange={(v) => update({ unit: v as any, startDate: nextSensibleAnchor(v as any) })}
        >
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            {/* One-minute floor — the scheduler cron ticks every minute, so
                anything finer can't fire anyway. */}
            <SelectItem value="minutes">minutes</SelectItem>
            <SelectItem value="hours">hours</SelectItem>
            <SelectItem value="days">days</SelectItem>
            <SelectItem value="weeks">weeks</SelectItem>
            <SelectItem value="months">months</SelectItem>
            <SelectItem value="pass_windows">pass windows</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {unit === 'pass_windows' && (
        <div className="space-y-2 rounded border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            Fires at the start of each uploaded satellite pass window for the pinned modem
            (Where → Pin to modem). Windows are managed on the device&apos;s Modems tab.
          </p>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Offset after window start (s)</Label>
            <Input
              type="number"
              min={0}
              value={(recurrence as any)?.offsetSec ?? 0}
              onChange={(e) => update({ offsetSec: Math.max(0, parseInt(e.target.value, 10) || 0) } as any)}
              className="w-24"
            />
          </div>
        </div>
      )}

      {/* "When in the period" inputs — anchored on startDate. */}
      {(unit === 'days' || unit === 'weeks' || unit === 'months') && (
        <div className="space-y-2 rounded border bg-muted/30 p-3">
          {unit === 'weeks' && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">On</Label>
              <div className="flex gap-1 flex-wrap">
                {DAYS_OF_WEEK.map((d) => (
                  <Button
                    key={d.value}
                    type="button"
                    variant={anchor.getDay() === d.value ? 'default' : 'outline'}
                    size="sm"
                    className="w-12 px-0"
                    onClick={() => setDayOfWeek(d.value)}
                  >
                    {d.short}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {unit === 'months' && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">On day</Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={anchor.getDate()}
                onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10) || 1)}
                className="w-20"
              />
              <span className="text-xs text-muted-foreground">of the month</span>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-muted-foreground">At</Label>
            <Input
              type="time"
              value={toLocalTimeValue(anchor)}
              onChange={(e) => setTime(e.target.value)}
              className="w-32"
            />
            <TimeZonePicker value={timeZone} onChange={onTimeZoneChange} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── ScheduledAtPicker (datetime + TZ for one-off scheduled runs) ──────────

function ScheduledAtPicker({
  executeAt, timeZone, onChange, onTimeZoneChange,
}: {
  executeAt?: Date;
  timeZone: string;
  onChange: (d: Date | undefined) => void;
  onTimeZoneChange: (tz: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Input
        type="datetime-local"
        value={executeAt ? toLocalInputValue(executeAt) : ''}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : undefined)}
        className="w-56"
      />
      <TimeZonePicker value={timeZone} onChange={onTimeZoneChange} />
    </div>
  );
}

// TimeZonePicker moved to the shared `@/components/ui/TimeZonePicker` so the
// Settings timezone preference and the scheduler share one control.

function nextSensibleAnchor(unit: RecurrenceUnitUi): Date {
  const d = new Date();
  // Default: tomorrow 9:00 AM for sub-daily granularities, today 9:00 for the
  // rest. Gives a sensible value the user can override.
  d.setHours(9, 0, 0, 0);
  if (unit === 'minutes' || unit === 'hours' || unit === 'pass_windows') {
    d.setMinutes(0, 0, 0);
  }
  return d;
}

// ── Saved config summary card (compact view) ──────────────────────────────

function SavedConfigSummary({
  config, locations, clusters,
}: { config: TestConfiguration; locations: Location[]; clusters: Cluster[] }) {
  const locReq = config.locationRequirements?.[0];
  const locLabel = locReq?.clusterId
    ? `Cluster · ${clusters.find((c) => c.id === locReq.clusterId)?.name ?? locReq.clusterId}`
    : (locations.find((l) => l.id === locReq?.locationId)?.displayName ?? locReq?.locationId ?? '—');
  const esim = config.esim?.mode === 'single' ? 'Specific profile'
              : config.esim?.mode === 'batch' ? `Batch · ${config.esim.batch?.groupId ?? ''}`
              : config.esim?.mode === 'none' ? 'None'
              : 'Auto';
  const timing = config.timing.type === 'scheduled' ? 'Scheduled'
                : config.timing.type === 'recurring' ? `Every ${config.timing.recurrence?.interval} ${config.timing.recurrence?.unit}`
                : 'Run now';
  return (
    <div className="text-xs space-y-1 rounded border bg-muted/30 p-3">
      <Row label="Name" value={config.name} />
      <Row label="Flow" value={config.flowName || config.flowId || '—'} />
      <Row label="Where" value={locLabel} />
      <Row label="eSIM" value={esim} />
      <Row label="Timing" value={timing} />
      <Separator className="my-1" />
      <Row label="Used" value={`${config.usageCount} times${config.lastUsedAt ? ` · last ${timeAgo(config.lastUsedAt)}` : ''}`} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function timeAgo(d: Date | undefined): string {
  if (!d) return 'never';
  const seconds = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
